import React, { FC } from 'react';
import { Desktop, Mobile, Trans } from '@edreamsodigeo/frontend-core';
import {
  Flex,
  Body,
  Box,
  Heading,
  Pill,
  Glyph,
} from '@edreamsodigeo/prisma-design-system';
import { AlternativeItineraryBenefitsProps } from './types';
import AlternativeItineraryLayerIcon from './AlternativeItineraryLayerIcon';
import { PriceDifference } from './AlternativeItineraryDoubleCardDecorators';
import { RawMoneyComponent } from '@components/Shared/Money';
import { AlternativeItineraryValuePropositionType } from 'clientState/providers/AlternativeItineraryProvider/types';
import { usePrime } from 'clientState/providers/PrimeProvider';
import useRandomNumber from 'clientState/hooks/useRandomNumber/useRandomNumber';

type BenefitHighlightProps = {
  color: string;
};

const BenefitPill: FC = ({ children }) => {
  const color = 'linear-gradient(135deg, #DA3835 0%, #F9700D 100%)';
  return (
    <Box
      as="span"
      px="small"
      py="tiny"
      borderRadius="big"
      sx={{ background: color }}
    >
      <Body type="xSmall" color="white" inline>
        {children}
      </Body>
    </Box>
  );
};

const BenefitHighlight: FC<BenefitHighlightProps> = ({ color, children }) => {
  return (
    <Body type="small" color={color} weight="medium" inline>
      {children}
    </Body>
  );
};

const BenefitMoneyAmount: FC<{
  valueProposition: AlternativeItineraryValuePropositionType;
  itinerary: AlternativeItineraryBenefitsProps['itinerary'];
}> = ({ valueProposition, itinerary }) => {
  const legs = itinerary?.legs || [];
  const isRoundTrip = legs.length > 1;
  const amount = valueProposition === 'cabinLuggage' ? 32 : 70;
  return (
    <RawMoneyComponent currency={itinerary?.price?.currency || 'EUR'}>
      {isRoundTrip ? amount * 2 : amount}
    </RawMoneyComponent>
  );
};

const BenefitDistance: FC<{
  distance: number | undefined;
}> = ({ distance }) => {
  const { isUK, isUS } = usePrime();
  const distanceNumber =
    isUK || isUS ? Math.floor((distance || 0) * 0.62) : distance;
  const distanceSuffix = isUK || isUS ? 'mi' : 'km';
  const text = `${distanceNumber}${distanceSuffix}`;
  return (
    <Body type="base" inline>
      {text}
    </Body>
  );
};

const AlternativeItineraryDoubleCardBenefitsMobileWithIcons: FC<
  AlternativeItineraryBenefitsProps
> = ({
  valuePropositions,
  additionalInformation,
  itinerary,
  selectedItinerary,
}) => {
  const generateRandomNumber = useRandomNumber();
  const numValuePropositions = valuePropositions?.length || 0;
  const numTravellers = 100 + Math.floor(generateRandomNumber(0, 20));
  return !(
    valuePropositions?.length === 1 && valuePropositions[0] === 'social'
  ) ? (
    <Box paddingTop="base">
      <Flex flexDirection={'column'}>
        {valuePropositions?.map((valueProposition, index) => (
          <Flex
            key={`benefit-${valueProposition}`}
            flexDirection={'row'}
            mb={index === numValuePropositions - 1 ? '0' : '1x_small'}
            alignItems={'start'}
          >
            <Flex
              flexDirection={'column'}
              alignItems={'flex-start'}
              justifyContent={'center'}
              height={'22px'}
              minWidth={'16px'}
            >
              <AlternativeItineraryLayerIcon
                valueProposition={valueProposition}
                iconSize="small"
                color="deals.40"
              />
            </Flex>
            <Flex
              flexDirection={'column'}
              alignItems={'start'}
              justifyContent={'center'}
              ml="1x_small"
            >
              <Body type="small">
                <Trans
                  i18nKey={`alternativeItinerary.benefit.${valueProposition}`}
                  values={{
                    numTravellers: numTravellers,
                  }}
                  components={{
                    highlightPill:
                      index === 0 ||
                      (numValuePropositions > 2 && index === 1) ? (
                        <BenefitPill />
                      ) : (
                        <BenefitHighlight color={'deals.40'} />
                      ),
                    priceDifference: (
                      <PriceDifference
                        itinerary={itinerary}
                        selectedItinerary={selectedItinerary}
                      />
                    ),
                    moneyAmount: (
                      <BenefitMoneyAmount
                        valueProposition={valueProposition}
                        itinerary={itinerary}
                      />
                    ),
                    distance: (
                      <BenefitDistance
                        distance={additionalInformation?.airport}
                      />
                    ),
                  }}
                />
              </Body>
            </Flex>
          </Flex>
        ))}
      </Flex>
    </Box>
  ) : null;
};

const AlternativeItineraryDoubleCardBenefitsBase: FC<
  AlternativeItineraryBenefitsProps
> = ({
  valuePropositions,
  additionalInformation,
  itinerary,
  selectedItinerary,
}) => {
  const generateRandomNumber = useRandomNumber();
  const numValuePropositions = valuePropositions?.length || 0;
  const numTravellers = 100 + Math.floor(generateRandomNumber(0, 20));
  return !(
    valuePropositions?.length === 1 && valuePropositions[0] === 'social'
  ) ? (
    <Box
      paddingTop="base"
      data-testid="alternative-itinerary-card-layer-benefits"
    >
      <Flex flexDirection={'column'}>
        {valuePropositions
          ?.slice(0, 3)
          .reverse()
          .map((valueProposition, index) => (
            <Flex
              key={`benefit-${valueProposition}`}
              mb={index === numValuePropositions - 1 ? '0' : '1x_small'}
              justifyContent="flex-start"
              gap="2"
            >
              <Box>
                <Glyph
                  category="UIActions"
                  name="Check"
                  size="tiny"
                  color="positive.50"
                />
              </Box>
              <Heading
                type="6"
                color="neutral.90"
                sx={{ '&:has(.benefit-pill)': { lineHeight: '24px' } }}
              >
                <Trans
                  i18nKey={`alternativeItinerary.benefit.${valueProposition}`}
                  values={{
                    numTravellers: numTravellers,
                  }}
                  components={{
                    highlightPill:
                      index === 0 ? (
                        <BenefitHighlight color="neutral.90" />
                      ) : index === 1 ? (
                        <Pill
                          className="benefit-pill"
                          rounded
                          type="positive"
                          theme="soft"
                          sx={{ fontWeight: '500' }}
                        />
                      ) : (
                        <Pill
                          className="benefit-pill"
                          rounded
                          type="deals"
                          theme="default"
                          sx={{ fontWeight: '500' }}
                        />
                      ),
                    priceDifference: (
                      <PriceDifference
                        itinerary={itinerary}
                        selectedItinerary={selectedItinerary}
                      />
                    ),
                    moneyAmount: (
                      <BenefitMoneyAmount
                        valueProposition={valueProposition}
                        itinerary={itinerary}
                      />
                    ),
                    distance: (
                      <BenefitDistance
                        distance={additionalInformation?.airport}
                      />
                    ),
                  }}
                />
              </Heading>
            </Flex>
          ))}
      </Flex>
    </Box>
  ) : null;
};

const AlternativeItineraryDoubleCardBenefitsMobile = (props) => {
  const { simpleUI } = props;
  const BenefitsComponent = simpleUI
    ? AlternativeItineraryDoubleCardBenefitsBase
    : AlternativeItineraryDoubleCardBenefitsMobileWithIcons;
  return <BenefitsComponent {...props} />;
};

const AlternativeItineraryDoubleCardBenefits = (props) => (
  <>
    <Desktop>
      <AlternativeItineraryDoubleCardBenefitsBase {...props} />
    </Desktop>
    <Mobile>
      <AlternativeItineraryDoubleCardBenefitsMobile {...props} />
    </Mobile>
  </>
);

export default AlternativeItineraryDoubleCardBenefits;
